import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { getProjectBankGalleryMedia } from 'scenes/ProjectBank/services/getProjectBankGalleryMedia.service';
import { projectBankGalleryMediaPageLoaded } from 'scenes/ProjectBank/store/actions/project-bank.actions';
import { loadUsersAndPersonalResourcesByUserIds } from 'store/projectUsers/thunks/loadUsersAndPersonalResourcesByUserIds';
import {
  getUnloadedInteractionProjectIds,
  loadProjectBankInteractions,
} from './loadProjectBankInteractions.thunk';

// No access (the layout sends the user away), or an API that has no such
// listing yet (before its deploy the gallery shows its images folder's own
// listing): nothing to tell the user.
const QUIET_STATUSES = [403, 404];

const selectGalleryMedia = (state) =>
  (state.projectBank && state.projectBank.galleryMedia) || null;

const isLatestRequest = (getState, requestId) => {
  const galleryMedia = selectGalleryMedia(getState());
  return Boolean(galleryMedia) && galleryMedia.requestId === requestId;
};

/**
 * Loads every photo and video of a project for the bank's gallery view, one
 * page after another (services/getProjectBankGalleryMedia), each page into
 * the files adapter as soon as it lands (projectBankGalleryMediaPageLoaded),
 * so that the newest photos show while the older ones are still coming. The
 * uploaders of each page are loaded as they first appear, and the project's
 * reactions once, as a folder's load does.
 *
 * A newer load for the gallery (the slice's `galleryMedia.requestId`) makes
 * this one stop where it is, its pages from then on unused. A failure is
 * reported, but not a refusal or a missing listing (QUIET_STATUSES).
 * @param {string} projectId the bank's project
 */
export const loadProjectBankGalleryMedia = createAsyncThunk(
  'projectBank/loadProjectBankGalleryMedia',
  async (projectId, { dispatch, getState, requestId }) => {
    const seenUserIds = new Set();
    let interactionsRequested = false;
    let nextToken = null;

    try {
      do {
        const page = await getProjectBankGalleryMedia(projectId, {
          nextToken,
        });
        if (!isLatestRequest(getState, requestId)) return { stale: true };

        const items = (page && page.items) || [];
        dispatch(projectBankGalleryMediaPageLoaded({ requestId, items }));

        // A photo whose uploader the API left out (a gallery-only user's
        // view of someone else's) names nobody to load.
        const newUserIds = [];
        items.forEach((item) => {
          const userId = item && item.user;
          if (typeof userId === 'string' && !seenUserIds.has(userId)) {
            seenUserIds.add(userId);
            newUserIds.push(userId);
          }
        });
        if (newUserIds.length > 0) {
          dispatch(loadUsersAndPersonalResourcesByUserIds(newUserIds));
        }

        if (!interactionsRequested && items.length > 0) {
          interactionsRequested = true;
          const unseen = getUnloadedInteractionProjectIds(getState(), [
            projectId,
          ]);
          if (unseen.length > 0) {
            dispatch(loadProjectBankInteractions(unseen));
          }
        }

        nextToken = (page && page.nextToken) || null;
      } while (nextToken);
    } catch (error) {
      const status = error && error.response && error.response.status;
      if (
        isLatestRequest(getState, requestId) &&
        !QUIET_STATUSES.includes(status)
      ) {
        enqueueSnackbar(i18n.t('bank.gallery_load_failed'), {
          variant: 'error',
          preventDuplicate: true,
        });
      }
      throw error;
    }

    return { stale: false };
  },
  {
    // Once per project while the bank is open: a loaded, loading or failed
    // project is not asked again (the slice goes back to idle when the bank
    // closes or the file picker's close resets it).
    condition: (projectId, { getState }) => {
      if (!projectId) return false;
      const galleryMedia = selectGalleryMedia(getState());
      return (
        !galleryMedia ||
        galleryMedia.projectId !== projectId ||
        galleryMedia.status === 'idle'
      );
    },
  }
);
